import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import {
  useArchivePriceList,
  useCreatePriceList,
  usePriceLists,
  useUnarchivePriceList,
} from '@/hooks/use-pricing';
import { useT } from '@/i18n/use-t';

const Schema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(1024).optional(),
  currencyCode: z.string().regex(/^[A-Z]{3}$/, 'ISO-4217 alpha-3 uppercase'),
  isDefault: z.boolean(),
});
type FormValues = z.infer<typeof Schema>;

/**
 * RL-332 — the currency a new price list should start with, taken from the
 * tenant's own active price lists rather than a constant (this used to be a
 * hardcoded `'EUR'`, silently wrong for every non-eurozone tenant).
 *
 * The default list's currency wins; failing that, the one currency every
 * active list shares. When there is nothing to go on (first list) or the
 * tenant is genuinely multi-currency, return `''` — the field is required, so
 * the operator has to state the currency instead of accepting a guess.
 */
export function suggestPriceListCurrency(
  activeLists: ReadonlyArray<{ currencyCode: string; isDefault: boolean }> | undefined,
): string {
  if (activeLists === undefined) return '';
  const only = (codes: string[]): string => {
    const distinct = new Set(codes);
    return distinct.size === 1 ? (codes[0] ?? '') : '';
  };
  const fromDefault = only(activeLists.filter((l) => l.isDefault).map((l) => l.currencyCode));
  if (fromDefault !== '') return fromDefault;
  return only(activeLists.map((l) => l.currencyCode));
}

export function PriceListsPage() {
  const t = useT('admin_ui');
  const [status, setStatus] = useState<'active' | 'archived'>('active');
  const lists = usePriceLists(status);
  // Always the ACTIVE lists, whichever filter is shown — archived lists say
  // nothing about the currency the tenant trades in today.
  const activeLists = usePriceLists('active');
  const suggestedCurrency = suggestPriceListCurrency(activeLists.data);
  const create = useCreatePriceList();
  const archive = useArchivePriceList();
  const unarchive = useUnarchivePriceList();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: { name: '', description: '', currencyCode: '', isDefault: false },
  });

  // The active lists load after the form mounts: fill the currency in once
  // they arrive, but never over something the operator has typed.
  const currencyTouched = dirtyFields.currencyCode === true;
  useEffect(() => {
    if (suggestedCurrency === '' || currencyTouched) return;
    if (getValues('currencyCode') === '') setValue('currencyCode', suggestedCurrency);
  }, [suggestedCurrency, currencyTouched, getValues, setValue]);

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('pricing.lists.title', 'Price lists')}</h1>
          <p className="text-sm text-muted-foreground">
            {lists.data === undefined
              ? '…'
              : `${lists.data.length} ${
                  status === 'active'
                    ? t('pricing.lists.countActive', 'active list(s)')
                    : t('pricing.lists.countArchived', 'archived list(s)')
                }`}
          </p>
        </div>
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as 'active' | 'archived')}
          className="w-40"
        >
          <option value="active">{t('pricing.lists.filterActive', 'Active')}</option>
          <option value="archived">{t('pricing.lists.filterArchived', 'Archived')}</option>
        </Select>
      </header>

      <section className="rounded-md border bg-background p-5">
        <h2 className="mb-3 text-sm font-medium">
          {t('pricing.lists.newHeading', 'New price list')}
        </h2>
        <form
          className="grid gap-3 md:grid-cols-[2fr_2fr_1fr_auto_auto]"
          onSubmit={handleSubmit(async (values) => {
            setError(null);
            try {
              await create.mutateAsync({
                name: values.name,
                description: values.description === '' ? undefined : values.description,
                currencyCode: values.currencyCode,
                isDefault: values.isDefault,
              });
              reset({
                name: '',
                description: '',
                // A newly created default supersedes the old default even
                // before the active-list query has refetched.
                currencyCode:
                  values.isDefault || suggestedCurrency === ''
                    ? values.currencyCode
                    : suggestedCurrency,
                isDefault: false,
              });
            } catch (err) {
              setError(extractError(err, t));
            }
          })}
        >
          <div className="space-y-1">
            <Label>{t('pricing.field.name', 'Name')}</Label>
            <Input {...register('name')} />
            {errors.name?.message !== undefined && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label>{t('pricing.field.description', 'Description')}</Label>
            <Input {...register('description')} />
          </div>
          <div className="space-y-1">
            <Label>{t('pricing.field.currency', 'Currency')}</Label>
            <Input className="font-mono" maxLength={3} {...register('currencyCode')} />
            {errors.currencyCode?.message !== undefined && (
              <p className="text-xs text-destructive">{errors.currencyCode.message}</p>
            )}
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...register('isDefault')} />
              {t('pricing.lists.default', 'Default')}
            </label>
          </div>
          <div className="flex items-end">
            <Button type="submit" disabled={create.isPending}>
              {t('pricing.lists.add', 'Add')}
            </Button>
          </div>
        </form>
        {error !== null && <p className="mt-2 text-sm text-destructive">{error}</p>}
      </section>

      <section className="rounded-md border bg-background">
        <Table>
          <THead>
            <TR>
              <TH>{t('pricing.field.name', 'Name')}</TH>
              <TH>{t('pricing.field.currency', 'Currency')}</TH>
              <TH>{t('pricing.field.description', 'Description')}</TH>
              <TH className="w-32" />
            </TR>
          </THead>
          <TBody>
            {lists.isLoading && (
              <TR>
                <TD colSpan={4} className="py-6 text-center text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {lists.data?.length === 0 && (
              <TR>
                <TD colSpan={4} className="py-6 text-center text-muted-foreground">
                  {status === 'active'
                    ? t('pricing.lists.emptyActive', 'No active price lists.')
                    : t('pricing.lists.emptyArchived', 'No archived price lists.')}
                </TD>
              </TR>
            )}
            {lists.data?.map((row) => (
              <TR key={row.id}>
                <TD>
                  <span className="font-medium">{row.name}</span>{' '}
                  {row.isDefault && row.status === 'active' && (
                    <Badge variant="success">{t('pricing.lists.defaultBadge', 'default')}</Badge>
                  )}
                </TD>
                <TD className="font-mono text-xs">{row.currencyCode}</TD>
                <TD className="text-sm text-muted-foreground">{row.description ?? '—'}</TD>
                <TD className="text-right">
                  {row.status === 'active' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={archive.isPending}
                      onClick={() => {
                        setError(null);
                        archive
                          .mutateAsync(row.id)
                          .catch((err: unknown) => setError(extractError(err, t)));
                      }}
                    >
                      {t('pricing.lists.archive', 'Archive')}
                    </Button>
                  )}
                  {row.status === 'archived' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={unarchive.isPending}
                      onClick={() => {
                        setError(null);
                        unarchive
                          .mutateAsync(row.id)
                          .catch((err: unknown) => setError(extractError(err, t)));
                      }}
                    >
                      {t('pricing.lists.unarchive', 'Unarchive')}
                    </Button>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </section>
    </div>
  );
}

function extractError(err: unknown, t: (key: string, bakedEnglish?: string) => string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string } | undefined;
    return data?.message ?? err.message;
  }
  return t('pricing.lists.unexpectedError', 'Unexpected error');
}
